/**
 * ChainVault WP - Solana Utility
 *
 * Handles all Solana blockchain interactions:
 * fetching SOL balance, transaction history,
 * and SPL token balances.
 *
 * @package ChainVault
 * @since   1.0.0
 */

/**
 * Helius RPC endpoint — fast and reliable Solana RPC
 * Free tier gives 1,000,000 credits per month
 */
const HELIUS_API_KEY = '3dea29f7-c0de-4597-989e-66287b5d8b85';
const HELIUS_RPC     = `https://mainnet.helius-rpc.com/?api-key=${ HELIUS_API_KEY }`;

/**
 * Fallback RPC endpoints if Helius is unavailable
 */
const FALLBACK_ENDPOINTS = [
    'https://api.mainnet-beta.solana.com',
    'https://solana-mainnet.g.alchemy.com/v2/demo',
    'https://solana.publicnode.com',
];

/**
 * Devnet RPC endpoints for testnet mode
 */
const DEVNET_ENDPOINTS = [
    'https://api.devnet.solana.com',
    'https://api.devnet.solana.com',
];

/**
 * All endpoints in priority order — respects testnet mode
 */
const isTestnetMode = () => localStorage.getItem( 'chainvault_testnet_mode' ) === '1';
const SOLANA_RPC_ENDPOINTS = isTestnetMode() ? DEVNET_ENDPOINTS : [ HELIUS_RPC, ...FALLBACK_ENDPOINTS ];

/**
 * USDT SPL Token mint address on Solana mainnet
 */
const USDT_MINT = 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB';

/**
 * USDC SPL Token mint address on Solana mainnet
 */
const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';

/**
 * Make a JSON-RPC request to Solana
 * Tries Helius first, then falls back to public endpoints
 *
 * @param {string} method  RPC method name
 * @param {Array}  params  RPC parameters
 * @return {Promise<any>}  RPC result
 */
async function solanaRPC( method, params = [] ) {
    let lastError;

    for ( const endpoint of SOLANA_RPC_ENDPOINTS ) {
        try {
            const response = await fetch( endpoint, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify( {
                    jsonrpc: '2.0',
                    id:      1,
                    method,
                    params,
                } ),
                signal: AbortSignal.timeout( 10000 ),
            } );

            if ( ! response.ok ) continue;

            const data = await response.json();

            if ( data.error ) {
                lastError = new Error( data.error.message );
                continue;
            }

            return data.result;

        } catch ( err ) {
            lastError = err;
            continue;
        }
    }

    throw lastError || new Error( 'All Solana RPC endpoints failed' );
}

/**
 * Fetch SOL balance for a wallet address
 *
 * @param {string} address   Solana wallet address (base58)
 * @return {Promise<string>} Balance in SOL as a string like "0.100000"
 */
export async function fetchSolBalance( address ) {
    try {
        const result = await solanaRPC( 'getBalance', [
            address,
            { commitment: 'confirmed' },
        ] );

        // Result is in lamports (1 SOL = 1,000,000,000 lamports)
        const lamports = result?.value ?? result ?? 0;
        const sol      = lamports / 1_000_000_000;
        return sol.toFixed( 6 );

    } catch ( error ) {
        console.error( 'Failed to fetch SOL balance:', error );
        return '0.000000';
    }
}

/**
 * Fetch real Solana transaction history with actual SOL amounts
 * Gets the last 20 confirmed transactions and parses real values
 *
 * @param {string} address   Solana wallet address
 * @return {Promise<Array>}  Array of transaction objects with real amounts
 */
export async function fetchSolTransactions( address ) {
    try {
        // Step 1: Get the last 20 transaction signatures
        const signatures = await solanaRPC( 'getSignaturesForAddress', [
            address,
            { limit: 20, commitment: 'confirmed' },
        ] );

        if ( ! signatures || signatures.length === 0 ) {
            return [];
        }

        // Step 2: Fetch full transaction details for each signature
        // This is where we get the REAL amounts
        const transactions = await Promise.all(
            signatures.map( async ( sigInfo ) => {
                try {
                    const tx = await solanaRPC( 'getTransaction', [
                        sigInfo.signature,
                        {
                            encoding:                       'jsonParsed',
                            maxSupportedTransactionVersion: 0,
                            commitment:                     'confirmed',
                        },
                    ] );

                    if ( ! tx ) return null;

                    // Find the index of our wallet address in accountKeys
                    const accountKeys = tx.transaction?.message?.accountKeys || [];
                    const myIndex     = accountKeys.findIndex( ( key ) => {
                        const pubkey = typeof key === 'string' ? key : key?.pubkey;
                        return pubkey === address;
                    } );

                    // Get pre and post balances in lamports
                    const preBalances  = tx.meta?.preBalances  || [];
                    const postBalances = tx.meta?.postBalances || [];

                    let solChange = 0;

                    if ( myIndex !== -1 ) {
                        const preLamports  = preBalances[ myIndex ]  || 0;
                        const postLamports = postBalances[ myIndex ] || 0;
                        // Positive = received, Negative = sent
                        solChange = ( postLamports - preLamports ) / 1_000_000_000;
                    }

                    // If change is negative or zero after fees = sent
                    // If change is positive = received
                    const isSent    = solChange <= 0;
                    const absChange = Math.abs( solChange );

                    // Get fee in SOL
                    const feeLamports = tx.meta?.fee || 0;
                    const fee         = feeLamports / 1_000_000_000;

                    // For sent: real amount = balance decrease minus fees
                    const displayAmount = isSent
                        ? Math.max( 0, absChange - fee ).toFixed( 6 )
                        : absChange.toFixed( 6 );

                    // Get the counterparty address
                    let counterparty = '';
                    if ( accountKeys.length > 0 ) {
                        const otherIndex = myIndex === 0 ? 1 : 0;
                        const otherKey   = accountKeys[ otherIndex ];
                        counterparty     = typeof otherKey === 'string'
                            ? otherKey
                            : otherKey?.pubkey || '';
                    }

                    return {
                        hash:      sigInfo.signature,
                        from:      isSent ? address : counterparty,
                        to:        isSent ? counterparty : address,
                        value:     parseFloat( displayAmount ),
                        timestamp: sigInfo.blockTime || 0,
                        isError:   tx.meta?.err !== null,
                        fee:       fee.toFixed( 6 ),
                        network:   'solana',
                        isSent,
                    };

                } catch ( txError ) {
                    console.error( 'Failed to fetch tx details:', txError );
                    return null;
                }
            } )
        );

        // Filter out any nulls from failed fetches
        return transactions.filter( Boolean );

    } catch ( error ) {
        console.error( 'Failed to fetch Solana transactions:', error );
        return [];
    }
}

/**
 * Fetch SPL token balances for a wallet
 * Gets USDT, USDC and other token balances on Solana
 *
 * @param {string} address   Solana wallet address
 * @return {Promise<Array>}  Array of token balance objects
 */
export async function fetchSolTokenBalances( address ) {
    try {
        const result = await solanaRPC( 'getTokenAccountsByOwner', [
            address,
            { programId: 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA' },
            { encoding: 'jsonParsed' },
        ] );

        const accounts = result?.value || [];
        const tokens   = [];

        for ( const account of accounts ) {
            const info   = account.account?.data?.parsed?.info;
            const mint   = info?.mint;
            const amount = info?.tokenAmount;

            if ( ! mint || ! amount ) continue;

            // Only show tokens with a balance greater than 0
            if ( parseFloat( amount.uiAmount || 0 ) === 0 ) continue;

            let symbol = 'TOKEN';
            let name   = 'Unknown Token';
            let color  = '#6C63FF';
            let icon   = null;

            // Identify known tokens by their mint address
            if ( mint === USDT_MINT ) {
                symbol = 'USDT';
                name   = 'Tether USD';
                color  = '#26A17B';
                icon   = 'https://assets.coingecko.com/coins/images/325/small/Tether.png';
            } else if ( mint === USDC_MINT ) {
                symbol = 'USDC';
                name   = 'USD Coin';
                color  = '#2775CA';
                icon   = 'https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png';
            }

            tokens.push( {
                id:      mint,
                name,
                symbol,
                balance: amount.uiAmountString || amount.uiAmount?.toString() || '0',
                color,
                icon,
                mint,
                network: 'solana',
            } );
        }

        return tokens;

    } catch ( error ) {
        console.error( 'Failed to fetch SPL token balances:', error );
        return [];
    }
}

/**
 * Check if Phantom wallet is installed in the browser
 *
 * @return {boolean} True if Phantom is installed
 */
export function isPhantomInstalled() {
    return !! ( window.phantom?.solana?.isPhantom || window.solana?.isPhantom );
}

/**
 * Connect to Phantom wallet and return the public key
 *
 * @return {Promise<string>} The wallet's public key as a string
 */
export async function connectPhantom() {
    const phantom = window.phantom?.solana || window.solana;

    if ( ! phantom ) {
        throw new Error( 'Phantom not installed' );
    }

    const response = await phantom.connect();
    return response.publicKey.toString();
}

/**
 * Listen for Phantom wallet events
 * Fires callbacks when user connects or disconnects
 *
 * @param {Function} onConnect     Called with publicKey when wallet connects
 * @param {Function} onDisconnect  Called when wallet disconnects
 */
export function listenToPhantomEvents( onConnect, onDisconnect ) {
    const phantom = window.phantom?.solana || window.solana;

    if ( ! phantom ) return;

    phantom.on( 'connect', ( publicKey ) => {
        if ( onConnect ) onConnect( publicKey.toString() );
    } );

    phantom.on( 'disconnect', () => {
        if ( onDisconnect ) onDisconnect();
    } );
}